import { BILLING_ERROR_FALLBACK, billingErrorMessage } from 'Billing/billingErrorMessage';
import type { NativeIAPStorePlatform } from './initPurchases';

/** What the app says when the server could not verify a store purchase, and whether trying again can help. */
export type VerificationFailureNotice = { message: string; retryable: boolean };

export const VERIFY_RETRY_MESSAGE =
  'Your purchase could not be verified yet. Please use Restore purchases to retry; you do not need to buy again.';

export const VERIFY_OTHER_ACCOUNT_MESSAGE: Record<NativeIAPStorePlatform, string> = {
  apple:
    'This App Store subscription is linked to a different MuseFlow account. To use it, sign in to that account. You can also manage the subscription in the App Store.',
  google:
    'This Google Play subscription is linked to a different MuseFlow account. To use it, sign in to that account. You can also manage the subscription in Google Play.',
};

export const VERIFY_SIGNED_OUT_MESSAGE =
  'Your sign-in has expired. Please sign in again, then use Restore purchases. You do not need to buy again.';

export const VERIFY_REFUSED_MESSAGE =
  'This purchase could not be added to your MuseFlow account. Please contact support@museflow.ai. You do not need to buy again.';

interface VerifyFailureShape {
  response?: { status?: unknown; data?: { message?: unknown } };
}

/**
 * Google Play answers 403 when a purchase belongs to another account; the App Store route answers
 * 400 with "Subscription belongs to another account".
 */
const belongsToAnotherAccount = (status: number, serverMessage: unknown) =>
  status === 403 ||
  (typeof serverMessage === 'string' && /belongs to another/i.test(serverMessage));

const sendsToSupport = (serverMessage: unknown) =>
  typeof serverMessage === 'string' && /support/i.test(serverMessage);

/**
 * The one place that picks what a failed purchase verification tells the user (MUS-618). Only a
 * failure that can pass on a later try (no answer, a timeout, 408, 429, 5xx) asks for a retry.
 */
export function verificationFailureNotice(
  failure: unknown,
  platform: NativeIAPStorePlatform,
): VerificationFailureNotice {
  const response = (failure as VerifyFailureShape | null | undefined)?.response;
  const status = Number(response?.status);
  if (!response || !status || status >= 500 || status === 408 || status === 429)
    return { message: VERIFY_RETRY_MESSAGE, retryable: true };
  // Google Play's 403 for a legacy purchase with no account on record sends the user to support.
  if (sendsToSupport(response.data?.message))
    return { message: VERIFY_REFUSED_MESSAGE, retryable: false };
  if (belongsToAnotherAccount(status, response.data?.message))
    return { message: VERIFY_OTHER_ACCOUNT_MESSAGE[platform], retryable: false };
  if (status === 401) return { message: VERIFY_SIGNED_OUT_MESSAGE, retryable: false };
  if (status === 409) {
    // Each 409 the verify routes send is a sentence written for people ("Manage your existing
    // subscription before purchasing through Google Play", ...).
    const serverSentence = billingErrorMessage(failure);
    return {
      message: serverSentence === BILLING_ERROR_FALLBACK ? VERIFY_REFUSED_MESSAGE : serverSentence,
      retryable: false,
    };
  }
  return { message: VERIFY_REFUSED_MESSAGE, retryable: false };
}
